/**
 * 探偵タイプ分類システム
 *
 * 4つの軸で16タイプに分類：
 * - 分析(A) vs 直感(I) : Analytical vs Intuitive
 * - 論理(L) vs 共感(E) : Logical vs Empathetic
 * - 慎重(C) vs 大胆(B) : Cautious vs Bold
 * - 独立(S) vs 協調(T) : Solo vs Team
 */
/**
 * 16タイプの詳細定義
 */
export const DETECTIVE_TYPES = {
    // === 分析型 (A) ===
    ALCS: {
        code: 'ALCS',
        name: '冷徹なる分析者',
        title: '孤高の論理師',
        shortDescription: '証拠と論理だけを信じ、一人で真実を追求する',
        fullDescription: '感情に流されず、冷静な分析で事件の核心に迫ります。他者の意見に左右されることなく、自らの論理を貫く姿勢は、複雑な事件ほど真価を発揮します。慎重に証拠を積み重ね、確実な推理を組み立てていく様は、まさに職人の技。ただし、その孤高さゆえに、人の心の機微を見落とすこともあるでしょう。',
        strengths: ['論理的思考力が非常に高い', '感情に左右されない冷静さ', '証拠の見落としが少ない'],
        weaknesses: ['人の心情を読み違えることがある', '協力を求めるのが苦手'],
        famousDetective: 'シャーロック・ホームズ',
        tsukumogamiAffinity: '古書の付喪神',
        rarity: 'uncommon'
    },
    ALCT: {
        code: 'ALCT',
        name: '理知の指揮者',
        title: '論理の司令塔',
        shortDescription: '冷静な分析と的確な指示で、チームを真実へ導く',
        fullDescription: '論理的な思考力と、チームをまとめる力を兼ね備えた稀有な存在です。自ら証拠を分析しながら、仲間の意見も取り入れて推理を組み立てていきます。慎重に行動しながらも、必要な時には決断を下すリーダーシップを持ちます。九十九商会の依頼では、複数の協力者と連携する事件で特に力を発揮するでしょう。',
        strengths: ['分析力とリーダーシップの両立', '情報を整理・統合する能力', 'チームの力を最大化できる'],
        weaknesses: ['一人での行動に不安を感じやすい', '全員の意見を聞きすぎて決断が遅れることも'],
        famousDetective: '明智小五郎',
        tsukumogamiAffinity: '将棋盤の付喪神',
        rarity: 'rare'
    },
    ALBS: {
        code: 'ALBS',
        name: '疾風の論理師',
        title: '速攻の分析者',
        shortDescription: '素早い分析と大胆な行動で、事件を一気に解決へ導く',
        fullDescription: '鋭い分析力を持ちながら、大胆な行動も厭わない攻撃的なスタイルの持ち主です。証拠を見つけたら即座に行動に移し、リスクを取ってでも真実に迫ります。一人で動くことを好み、自分のペースで事件を追いかけます。スピード感のある推理は見事ですが、時に慎重さを欠くこともあるでしょう。',
        strengths: ['分析から行動までが速い', '困難な状況でも怯まない', '決断力がある'],
        weaknesses: ['慎重さに欠けることがある', '周囲との連携が苦手'],
        famousDetective: '金田一耕助',
        tsukumogamiAffinity: '刀の付喪神',
        rarity: 'uncommon'
    },
    ALBT: {
        code: 'ALBT',
        name: '突撃の参謀',
        title: '攻めの司令官',
        shortDescription: '分析に基づく大胆な作戦で、チームを勝利に導く',
        fullDescription: '冷静な分析力と大胆な行動力、そしてチームを率いる力を併せ持つ、まさに参謀タイプです。状況を素早く分析し、リスクを計算した上で果敢に行動を起こします。仲間との連携を重視しながらも、決断は迅速。九十九商会の大規模な事件で、その真価を発揮するでしょう。',
        strengths: ['戦略的思考と行動力の両立', 'チームを鼓舞する力', 'ピンチをチャンスに変える'],
        weaknesses: ['慎重派との意見対立が起きやすい', '焦りが判断を曇らせることも'],
        famousDetective: 'エルキュール・ポアロ（後期）',
        tsukumogamiAffinity: '軍配の付喪神',
        rarity: 'rare'
    },
    AECS: {
        code: 'AECS',
        name: '静謐の共感者',
        title: '心を読む分析者',
        shortDescription: '論理と共感を織り交ぜ、静かに真実を見つめる',
        fullDescription: '分析的な思考を持ちながら、人の心にも寄り添える繊細な感性の持ち主です。証拠を冷静に分析しつつ、関係者の心情も深く理解しようとします。慎重に、そして一人で静かに推理を進めることを好みます。その優しさと鋭さの両立は、被害者の無念を晴らす力となるでしょう。',
        strengths: ['論理と感情の両面から推理できる', '被害者・関係者への深い理解', '細やかな観察力'],
        weaknesses: ['感情移入しすぎることがある', '決断に時間がかかる'],
        famousDetective: '浅見光彦',
        tsukumogamiAffinity: '茶器の付喪神',
        rarity: 'common'
    },
    AECT: {
        code: 'AECT',
        name: '調和の探求者',
        title: '絆を紡ぐ分析者',
        shortDescription: '分析力と共感力で、人々の心を繋ぎながら真実を追う',
        fullDescription: '冷静な分析と温かい共感を両立させ、関係者全員の心に寄り添いながら事件を解決へ導きます。チームワークを大切にし、仲間の意見にも耳を傾けます。慎重に進めながらも、人の心を動かす力で協力を得ることができます。九十九商会でも、付喪神との対話で真価を発揮するタイプです。',
        strengths: ['人の心を開かせる力', '分析と共感のバランス', 'チームの和を保つ'],
        weaknesses: ['厳しい決断を下すのが苦手', '全員を救おうとして苦しむことも'],
        famousDetective: 'ミス・マープル',
        tsukumogamiAffinity: '糸巻きの付喪神',
        rarity: 'common'
    },
    AEBS: {
        code: 'AEBS',
        name: '情熱の解決者',
        title: '心で突き進む分析者',
        shortDescription: '分析力と熱い心で、大胆に真実へと突き進む',
        fullDescription: '論理的な分析ができながらも、人への共感から大胆な行動を起こす熱血タイプです。「この人を救いたい」という思いが、あなたを突き動かします。一人で行動することを好み、自分の信じた道を突き進みます。その情熱は時に無謀に見えますが、それこそが事件を動かす原動力となるのです。',
        strengths: ['強い正義感と行動力', '被害者のために全力を尽くす', '困難に立ち向かう勇気'],
        weaknesses: ['感情的になりやすい', '一人で抱え込みがち'],
        famousDetective: '工藤新一（コナン）',
        tsukumogamiAffinity: '提灯の付喪神',
        rarity: 'uncommon'
    },
    AEBT: {
        code: 'AEBT',
        name: '絆の戦士',
        title: '仲間と駆ける分析者',
        shortDescription: '分析力と仲間への信頼で、大胆に事件に挑む',
        fullDescription: '冷静な分析と温かい心、そして仲間との絆を武器に戦う探偵です。論理的に考えながらも、仲間を信じて大胆な作戦を実行します。チームの力を最大限に引き出し、困難な事件も乗り越えていきます。九十九商会の仲間たちと共に、数々の事件を解決してきたことでしょう。',
        strengths: ['チームの士気を高める力', '分析と行動のバランス', '仲間への深い信頼'],
        weaknesses: ['仲間を守ろうとして無理をしがち', '一人では力を発揮しにくい'],
        famousDetective: '毛利小五郎（本気）',
        tsukumogamiAffinity: '太鼓の付喪神',
        rarity: 'uncommon'
    },
    // === 直感型 (I) ===
    ILCS: {
        code: 'ILCS',
        name: '静寂の閃光',
        title: '孤高の直感師',
        shortDescription: '静かな直感と論理で、一人真実を見抜く',
        fullDescription: '言葉にできない「何か」を感じ取る鋭い直感を持ちながら、それを論理で裏付ける稀有な才能の持ち主です。慎重に、そして孤独に推理を進め、誰も気づかない真実に辿り着きます。その閃きは時に周囲を驚かせますが、本人にとっては「当然の帰結」。静かな佇まいの中に、鋭い知性が宿っています。',
        strengths: ['言葉にできない違和感を察知する', '直感を論理で補強できる', '独自の視点で真実に迫る'],
        weaknesses: ['直感の説明が難しい', '理解されにくいことがある'],
        famousDetective: '御手洗潔',
        tsukumogamiAffinity: '鏡の付喪神',
        rarity: 'rare'
    },
    ILCT: {
        code: 'ILCT',
        name: '導きの預言者',
        title: '閃きを伝える者',
        shortDescription: '直感的な閃きを論理的に伝え、チームを導く',
        fullDescription: '鋭い直感で真実を見抜きながら、それを論理的に説明してチームを導く能力を持ちます。「なんとなく」を「だからこうなる」に変換できる翻訳者のような存在です。慎重にチームをまとめながら、的確な指示を出していきます。九十九商会でも、付喪神の言葉なき訴えを理解できる貴重な人材でしょう。',
        strengths: ['直感を論理に変換できる', 'チームに閃きを共有できる', '複雑な状況を整理する力'],
        weaknesses: ['説明に時間がかかることがある', '直感と論理の間で迷うことも'],
        famousDetective: '湯川学（ガリレオ）',
        tsukumogamiAffinity: '羅針盤の付喪神',
        rarity: 'legendary'
    },
    ILBS: {
        code: 'ILBS',
        name: '閃光の狩人',
        title: '直感で駆ける論理師',
        shortDescription: '閃きと論理を武器に、大胆に真実を追い詰める',
        fullDescription: '直感的な閃きを信じ、論理で裏付けながら大胆に行動する攻撃的なスタイルの持ち主です。「これだ」と感じたら即座に動き、一人で真実を追い詰めていきます。その鋭さと速さは、犯人に逃げる隙を与えません。ただし、その孤高の狩りは時に危険を伴うことも。',
        strengths: ['閃きから行動までが速い', '論理的な裏付けも怠らない', '獲物を逃さない執念'],
        weaknesses: ['危険を顧みないことがある', '単独行動のリスク'],
        famousDetective: 'L（デスノート）',
        tsukumogamiAffinity: '弓の付喪神',
        rarity: 'rare'
    },
    ILBT: {
        code: 'ILBT',
        name: '疾風の指揮者',
        title: '閃きで率いる者',
        shortDescription: '直感的な閃きでチームを率い、大胆に事件を解決する',
        fullDescription: '鋭い直感と論理的な思考、そしてチームを率いる力を持つカリスマ的な存在です。閃きを信じて大胆な作戦を立て、仲間と共に実行していきます。「ついてこい」と言わんばかりの行動力で、チームを真実へと導きます。九十九商会でも、大きな事件で指揮を任されることが多いでしょう。',
        strengths: ['カリスマ的なリーダーシップ', '直感と行動力の融合', 'チームを鼓舞する力'],
        weaknesses: ['独断で動きがち', 'ついてこられない者を置いていくことも'],
        famousDetective: '古畑任三郎',
        tsukumogamiAffinity: '扇子の付喪神',
        rarity: 'legendary'
    },
    IECS: {
        code: 'IECS',
        name: '慈愛の預言者',
        title: '心で感じ取る者',
        shortDescription: '深い共感と直感で、静かに真実を見つめる',
        fullDescription: '人の心に深く寄り添いながら、直感的に真実を感じ取る繊細な魂の持ち主です。言葉にならない悲しみや怒り、後悔を読み取り、事件の本質に迫ります。慎重に、そして一人で静かに推理を進めることを好みます。付喪神の「想い」を最も深く理解できるのは、あなたのようなタイプかもしれません。',
        strengths: ['人の心の奥底を読む力', '言葉にならない想いを感じ取る', '被害者への深い共感'],
        weaknesses: ['感情に引きずられやすい', '客観性を保つのが難しいことも'],
        famousDetective: '京極堂',
        tsukumogamiAffinity: '人形の付喪神',
        rarity: 'uncommon'
    },
    IECT: {
        code: 'IECT',
        name: '絆の巫女',
        title: '心を繋ぐ直感者',
        shortDescription: '直感と共感で人々の心を繋ぎ、真実を紡ぐ',
        fullDescription: '深い共感力と鋭い直感、そしてチームをまとめる力を持つ、まるで巫女のような存在です。人の心を読み、仲間の力を引き出し、慎重に事件を解決へ導きます。関係者全員の想いを汲み取りながら、最も穏やかな解決を目指します。九十九商会で、付喪神と人の架け橋となれる貴重な人材です。',
        strengths: ['人と人を繋ぐ力', '直感と共感の融合', '穏やかな解決を導く'],
        weaknesses: ['厳しい決断ができないことも', '全員を救えないと苦しむ'],
        famousDetective: '榎木津礼二郎',
        tsukumogamiAffinity: '琴の付喪神',
        rarity: 'rare'
    },
    IEBS: {
        code: 'IEBS',
        name: '閃光の救済者',
        title: '心で突き進む直感者',
        shortDescription: '直感と情熱で、大胆に人々を救う',
        fullDescription: '鋭い直感と深い共感、そして大胆な行動力を持つ情熱的な探偵です。「この人を救いたい」という想いが閃きを生み、即座に行動へと移します。一人で危険に飛び込むことも厭わず、真実と人の心の両方を救おうとします。その熱さは時に無謀ですが、それこそがあなたの魅力です。',
        strengths: ['強い正義感と直感の融合', '人を救うための行動力', '困難に立ち向かう勇気'],
        weaknesses: ['感情的になりやすい', '危険を顧みない'],
        famousDetective: '金田一一',
        tsukumogamiAffinity: '灯籠の付喪神',
        rarity: 'common'
    },
    IEBT: {
        code: 'IEBT',
        name: '奇跡の導き手',
        title: '絆で奇跡を起こす者',
        shortDescription: '直感と仲間への信頼で、奇跡的な解決を導く',
        fullDescription: '深い共感と鋭い直感、仲間への絶対的な信頼、そして大胆な行動力。すべてを併せ持つ、最も稀有な探偵タイプです。仲間と共に困難に立ち向かい、誰もが不可能だと思った事件を解決へと導きます。その姿は、まさに奇跡を起こす者。九十九商会の伝説として語り継がれることでしょう。',
        strengths: ['仲間との絆が奇跡を起こす', '直感と行動力の完璧な融合', 'どんな困難も乗り越える'],
        weaknesses: ['仲間がいないと力を発揮できない', '感情に流されやすい'],
        famousDetective: '江戸川コナン（チーム戦）',
        tsukumogamiAffinity: '万華鏡の付喪神',
        rarity: 'legendary'
    }
};
/**
 * 軸の値からタイプコードを算出
 */
export function calculateDetectiveTypeCode(axis) {
    const a = axis.analytical >= 50 ? 'A' : 'I';
    const l = axis.logical >= 50 ? 'L' : 'E';
    const c = axis.cautious >= 50 ? 'C' : 'B';
    const s = axis.solo >= 50 ? 'S' : 'T';
    return `${a}${l}${c}${s}`;
}
/**
 * タイプの相性を判定（0-100）
 */
export function calculateTypeCompatibility(type1, type2) {
    let score = 50;
    // 同じ軸が多いほど相性が良い
    for (let i = 0; i < 4; i++) {
        if (type1[i] === type2[i]) {
            score += 12;
        }
    }
    // 完全一致はボーナス
    if (type1 === type2) {
        score += 10;
    }
    return Math.min(100, score);
}
/**
 * レアリティに応じた出現率
 */
export const RARITY_PERCENTAGES = {
    common: '約20%',
    uncommon: '約10%',
    rare: '約5%',
    legendary: '約2%'
};
//# sourceMappingURL=detectiveTypes.js.map